import { useTranslation } from 'react-i18next';

import { BoatCard } from '@todo/web-ui/components/boat-card';
import { CardSkeletons } from '@todo/web-ui/components/card-skeletons';
import { CategoryGuide } from '@todo/web-ui/components/category-guide';
import { CategoryStrip } from '@todo/web-ui/components/category-strip';
import { EventCard } from '@todo/web-ui/components/event-card';
import { FaqList } from '@todo/web-ui/components/faq-list';
import { FeaturedRow } from '@todo/web-ui/components/featured-row';
import { HeroSearch } from '@todo/web-ui/components/hero-search';
import { Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { SampleCards } from '@todo/web-ui/components/sample-cards';
import { Section } from '@todo/web-ui/components/section';
import { seedScript } from '@todo/web-ui/components/seed-script';
import { TourCard } from '@todo/web-ui/components/tour-card';
import { localePath } from '@todo/web-ui/lib/format';
import { LatestGuides, latestGuidesSeed } from '@todo/web-ui/components/related-guides';
import {
  FEATURED_BOATS_QUERY,
  FEATURED_BOATS_SEED_KEY,
  FEATURED_CARDS,
  FEATURED_TOURS_QUERY,
  FEATURED_TOURS_SEED_KEY,
  HOME_EVENTS_SEED_KEY,
  HOME_EVENTS_WHEN,
  HOME_NYE_SEED_KEY,
  fetchNyeEvents,
} from '@/lib/prefetch';
import { NYE_PATH } from '@/lib/collections';
import { useEventCards } from '@/lib/use-event-cards';
import { useSeededList } from '@/lib/use-seeded-list';
import { HOME_CATEGORIES_SEED_KEY } from '@todo/web-ui/lib/collections';
import { useBoats } from '@todo/core/hooks/use-boats';
import { useTours } from '@todo/core/hooks/use-tours';
import { useLocale } from '@todo/core/i18n/use-locale';
import { faqPageJsonLd } from '@todo/core/seo';
import type { DbEvent } from '@todo/core/supabase';

import css from './home.module.css';

/** "Good to know" cards, in order: copy under `home.tips.<id>`. */
const TIPS = ['season', 'customs', 'booking', 'ranking'] as const;

/** FAQ entries, in order: copy under `home.faq.<id>` (also the FAQPage JSON-LD). */
const FAQ = ['mustDo', 'safari', 'flights', 'advance', 'cancel', 'wear', 'kids', 'abuDhabi', 'newYear', 'booking'] as const;

const fetchHomeNye = () => fetchNyeEvents().then((rows) => rows.slice(0, FEATURED_CARDS));

// The home page: header over the hero (its photo runs under the transparent
// header, Page `underHeader`, as on miami), hero with search, category strip, Featured This
// Week, Events this week (when the week has any), then the copy the design canvas doesn't have — a guide to the
// category collections (CategoryGuide), "Good to know" and an FAQ — and the
// footer. The design's "Curated for Your Mood" tiles are left out while the
// mood collections have no theme rules (empty lists); MoodTiles is kept for
// when they do.
export default function Home() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  // "Featured This Week" is one mixed row of three (the design shows tours
  // and boats side by side): the top boat, and the best-ranked tours for the
  // rest. Until the catalogs have rows, the design's own three cards.
  const { tours, loading: toursLoading } = useTours(FEATURED_TOURS_QUERY, FEATURED_TOURS_SEED_KEY);
  const { boats: featuredBoats, loading: boatsLoading } = useBoats(FEATURED_BOATS_QUERY, FEATURED_BOATS_SEED_KEY);

  // "Events this week": the week's top shows, recommended first (regular
  // events before exclusive ones — sync-platinumlist's list_rank).
  const { events } = useEventCards(HOME_EVENTS_SEED_KEY, HOME_EVENTS_WHEN, HOME_EVENTS_WHEN, FEATURED_CARDS);
  // In season (while next New Year's Eve's shows are on sale), its top shows
  // and the way to the /new-years-eve hub.
  const { rows: nyeEvents } = useSeededList<DbEvent>(HOME_NYE_SEED_KEY, fetchHomeNye);

  const featuredTours = tours.slice(0, FEATURED_CARDS - featuredBoats.length);
  const empty = featuredTours.length + featuredBoats.length === 0;
  const faq = FAQ.map((id) => ({ q: t(`home.faq.${id}.q`), a: t(`home.faq.${id}.a`) }));

  return (
    <Page flush underHeader={<HeroSearch />}>
      <PageHead title={t('home.metaTitle')} description={t('home.metaDescription')} path="" indexable>
        {seedScript(FEATURED_TOURS_SEED_KEY)}
        {seedScript(FEATURED_BOATS_SEED_KEY)}
        {seedScript(HOME_CATEGORIES_SEED_KEY)}
        {seedScript(HOME_EVENTS_SEED_KEY)}
        {seedScript(HOME_NYE_SEED_KEY)}
        {latestGuidesSeed(locale)}
        <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(faq))}</script>
      </PageHead>

      <CategoryStrip />

      <Section
        id="featured"
        title={t('home.featuredTitle')}
        eyebrow={t('home.featuredEyebrow')}
        more={{ href: localePath(locale, 'tours'), label: t('home.viewAllExperiences') }}
        moreBelowOnMobile>
        <FeaturedRow>
          {empty && (toursLoading || boatsLoading) ? (
            <CardSkeletons count={FEATURED_CARDS} />
          ) : empty ? (
            <SampleCards />
          ) : (
            <>
              {featuredTours.map((tour) => (
                <TourCard key={tour.id} tour={tour} featured />
              ))}
              {featuredBoats.map((boat) => (
                <BoatCard key={boat.id} boat={boat} />
              ))}
            </>
          )}
        </FeaturedRow>
      </Section>

      {events.length ? (
        <Section
          id="events-week"
          title={t('home.eventsTitle')}
          eyebrow={t('home.eventsEyebrow')}
          more={{ href: localePath(locale, 'events'), label: t('home.viewAllEvents') }}
          moreBelowOnMobile>
          <FeaturedRow>
            {events.map((event) => (
              <EventCard key={event.show_slug} event={event} />
            ))}
          </FeaturedRow>
        </Section>
      ) : null}

      {nyeEvents.length ? (
        <Section
          id="new-years-eve"
          title={t('home.nyeTitle')}
          eyebrow={t('home.nyeEyebrow')}
          more={{ href: localePath(locale, NYE_PATH), label: t('home.nyeMore') }}
          moreBelowOnMobile>
          <FeaturedRow>
            {nyeEvents.map((event) => (
              <EventCard key={event.show_slug} event={event} />
            ))}
          </FeaturedRow>
        </Section>
      ) : null}

      <Section id="guide" title={t('home.guideTitle')}>
        <CategoryGuide />
      </Section>

      <LatestGuides />

      <Section id="tips" title={t('home.tipsTitle')} eyebrow={t('home.tipsEyebrow')}>
        <ul className={css.tips}>
          {TIPS.map((id) => (
            <li key={id} className={css.tip}>
              <h3 className={css.tipTitle}>{t(`home.tips.${id}.title`)}</h3>
              <p className={css.tipText}>{t(`home.tips.${id}.text`)}</p>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="faq" title={t('home.faqTitle')} eyebrow={t('home.faqEyebrow')} className={css.last}>
        <FaqList entries={faq} />
      </Section>
    </Page>
  );
}
